import stylesPc from "./PresentersFilters.module.css";
import stylesSp from "./PresentersFilters_SP.module.css";
import React, { memo, useState, useEffect } from "react";
import { Checkbox, createStyles, FormControlLabel, makeStyles, Theme } from "@material-ui/core";
import { useSelector } from "react-redux";
import { AppState } from "../../../root/RootReducer";
import { AnimatePresence, motion } from "framer-motion";
import PuiTypography from "../../../components/elements/PuiTypography";
import classNames from "classnames";
import PuiTooltip from "../../../components/elements/PuiTooltip ";
import CheckedIcon from "../../../assets/image/CheckedIcon";
import { IsDevice } from "../../../components/function/useDeviceFlag";
import { Tag } from "../../../viewer/search/Search";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        filterCheckBox: {
            "& .MuiCheckbox-root": { color: "white" },
            marginBottom: "-8px"
        }
    })
);

interface CheckObject {
    [key: string]: boolean;
}

interface PresentersFiltersProps {
    anime: boolean;
    checkedSkills: CheckObject;
    checkedTags: CheckObject;
    changeSkillFilter: (event: React.ChangeEvent<HTMLInputElement>) => void;
    changeTagFilter: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

function PresentersFilters(props: PresentersFiltersProps) {
    // レスポンシブ対応：ブラウザサイズによるCSSファイルの切り替え
    const styles = IsDevice("desktop") ? stylesPc : stylesSp;
    const classes = useStyles();

    const { skills, tags } = useSelector((state: AppState) => ({
        skills: state.presenters.index.skills,
        tags: state.presenters.index.tags
    }));

    const { anime, checkedSkills, checkedTags, changeSkillFilter, changeTagFilter } = props;

    // 重複なタグを削除
    const [tagsWithoutDuplicates, setTagsWithoutDuplicates] = useState<Tag[]>([]);
    const SetTagsWithoutDuplicates = () => {
        if (tags && tags.length !== 0) {
            const tagsFilter = tags.filter((element, index, self) => 
                self.findIndex(e => 
                    e.title === element.title
                ) === index
            );
            setTagsWithoutDuplicates(tagsFilter);
        }
    };

    // tagsが変更されたら、tagsWithoutDuplicatesも変更するように
    useEffect(() => {
        SetTagsWithoutDuplicates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tags]);

    const contents = (
        <>
            <div className={styles.header}>
                <PuiTypography size={20} bold={true}>
                    Presenter Skills
                </PuiTypography>
            </div>
            <div className={styles.list}>
                {skills &&
                    skills.map((skill) => (
                        <div key={skill.id} className={styles.filterCheckbox}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checkedIcon={
                                            <div className={styles.checkedIcon}>
                                                <CheckedIcon />
                                            </div>
                                        }
                                        checked={checkedSkills[skill.skill] || false}
                                        onChange={changeSkillFilter}
                                        name={skill.skill}
                                        id={String(skill.id)}
                                    />
                                }
                                label={
                                    <>
                                        <div
                                            className={styles.filterCheckboxTag}
                                            data-tip={skill.skill}
                                            data-for="skill"
                                        >
                                            <PuiTypography>{skill.skill}</PuiTypography>
                                        </div>
                                        <PuiTooltip id="skill" />
                                    </>
                                }
                                className={classes.filterCheckBox}
                            />
                        </div>
                    ))}
            </div>
            <div className={`${styles.header} ${styles.headerMarginTop}`}>
                <PuiTypography size={20} bold={true}>
                    Tags
                </PuiTypography>
            </div>
            <div className={classNames(styles.list, styles.listMargin)}>
                {tagsWithoutDuplicates &&
                    tagsWithoutDuplicates.map((tag) => (
                        <div key={tag.id} className={styles.filterCheckbox}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checkedIcon={
                                            <div className={styles.checkedIcon}>
                                                <CheckedIcon/>
                                            </div>
                                        }
                                        checked={checkedTags[tag.title] || false}
                                        onChange={changeTagFilter}
                                        name={tag.title}
                                        id={String(tag.id)}
                                    />
                                }
                                label={
                                    <>
                                        <div
                                            className={styles.filterCheckboxTag}
                                            data-tip={tag.title}
                                            data-for="tag"
                                        >
                                            <PuiTypography>{tag.title}</PuiTypography>
                                        </div>
                                        <PuiTooltip id="tag"/>
                                    </>
                                }
                                className={classes.filterCheckBox}
                            />
                        </div>
                    ))}
            </div>
        </>
    );

    return (
        <AnimatePresence>
            {anime && (
                <motion.div
                    initial={{ x: -20 }}
                    animate={{ x: 0 }}
                    transition={{ ease: "easeOut", duration: 0.5 }}
                    className={styles.filters}
                >
                    {contents}
                </motion.div>
            )}
            {!anime && <div className={styles.filters}>{contents}</div>}
        </AnimatePresence>
    );
}

export default memo(PresentersFilters);
